import React, { useState, useEffect } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { noticeAPI } from "../../lib/api";
import { toast } from "sonner";
import { formatDate, capitalizeFirst } from "../../lib/utils";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Badge } from "../../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Megaphone, Plus, Send, Eye, Loader2, CheckCircle } from "lucide-react";

const NoticesPage = () => {
  const { user, isHRAdmin, isManager } = useAuth();
  const [notices, setNotices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [viewNotice, setViewNotice] = useState(null);
  const [formData, setFormData] = useState({
    title: "",
    content: "",
    priority: "normal",
    expiry_date: "",
    target_roles: [],
    target_departments: [],
  });

  useEffect(() => {
    fetchNotices();
  }, []);

  const fetchNotices = async () => {
    try {
      const response = await noticeAPI.getAll();
      setNotices(response.data);
    } catch (error) {
      toast.error("Failed to fetch notices");
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreateLoading(true);
    try {
      await noticeAPI.create(formData);
      toast.success("Notice created successfully");
      setShowCreateDialog(false);
      setFormData({
        title: "",
        content: "",
        priority: "normal",
        expiry_date: "",
        target_roles: [],
        target_departments: [],
      });
      fetchNotices();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create notice");
    } finally {
      setCreateLoading(false);
    }
  };

  const handlePublish = async (noticeId) => {
    try {
      await noticeAPI.publish(noticeId);
      toast.success("Notice published");
      fetchNotices();
    } catch (error) {
      toast.error("Failed to publish notice");
    }
  };

  const handleAcknowledge = async (noticeId) => {
    try {
      await noticeAPI.acknowledge(noticeId);
      toast.success("Notice acknowledged");
      fetchNotices();
    } catch (error) {
      toast.error("Failed to acknowledge notice");
    }
  };

  const getPriorityColor = (priority) => {
    const colors = {
      high: "bg-red-100 text-red-700",
      normal: "bg-blue-100 text-blue-700",
      low: "bg-slate-100 text-slate-700",
    };
    return colors[priority] || colors.normal;
  };

  const canCreate = isHRAdmin || isManager;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner w-8 h-8" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="notices-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900">Notices</h1>
          <p className="text-slate-500">Company announcements and updates</p>
        </div>
        {canCreate && (
          <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
            <DialogTrigger asChild>
              <Button className="btn-primary" data-testid="create-notice-btn">
                <Plus className="w-4 h-4 mr-2" />
                Create Notice
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-lg">
              <DialogHeader>
                <DialogTitle className="font-heading">Create Notice</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreate} className="space-y-4 mt-4">
                <div className="form-group">
                  <Label>Title</Label>
                  <Input
                    placeholder="Notice title..."
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    required
                    data-testid="notice-title-input"
                  />
                </div>
                <div className="form-group">
                  <Label>Content</Label>
                  <Textarea
                    placeholder="Notice content..."
                    value={formData.content}
                    onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                    required
                    rows={5}
                    data-testid="notice-content-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="form-group">
                    <Label>Priority</Label>
                    <Select
                      value={formData.priority}
                      onValueChange={(value) => setFormData({ ...formData, priority: value })}
                    >
                      <SelectTrigger data-testid="notice-priority-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="low">Low</SelectItem>
                        <SelectItem value="normal">Normal</SelectItem>
                        <SelectItem value="high">High</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="form-group">
                    <Label>Expiry Date (Optional)</Label>
                    <Input
                      type="date"
                      value={formData.expiry_date}
                      onChange={(e) => setFormData({ ...formData, expiry_date: e.target.value })}
                    />
                  </div>
                </div>
                <Button type="submit" className="w-full btn-primary" disabled={createLoading} data-testid="submit-notice">
                  {createLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create Notice"}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Notices Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {notices.length === 0 ? (
          <Card className="col-span-full">
            <CardContent className="p-8 text-center text-slate-500">
              No notices available
            </CardContent>
          </Card>
        ) : (
          notices.map((notice) => (
            <Card key={notice.id} className="card-hover" data-testid={`notice-card-${notice.id}`}>
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                      <Megaphone className="w-5 h-5 text-amber-600" />
                    </div>
                    <div>
                      <CardTitle className="text-base font-medium line-clamp-1">
                        {notice.title}
                      </CardTitle>
                      <p className="text-xs text-slate-500">{formatDate(notice.created_at)}</p>
                    </div>
                  </div>
                  <Badge className={getPriorityColor(notice.priority)}>
                    {notice.priority}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-slate-600 line-clamp-3 mb-4">{notice.content}</p>
                <div className="flex items-center justify-between">
                  <Badge variant={notice.status === "published" ? "default" : "secondary"}>
                    {capitalizeFirst(notice.status)}
                  </Badge>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setViewNotice(notice)}
                    >
                      <Eye className="w-4 h-4" />
                    </Button>
                    {canCreate && notice.status === "draft" && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handlePublish(notice.id)}
                      >
                        <Send className="w-4 h-4" />
                      </Button>
                    )}
                    {notice.status === "published" && notice.author_id !== user.id && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleAcknowledge(notice.id)}
                      >
                        <CheckCircle className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* View Notice Dialog */}
      <Dialog open={!!viewNotice} onOpenChange={() => setViewNotice(null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading">{viewNotice?.title}</DialogTitle>
          </DialogHeader>
          {viewNotice && (
            <div className="space-y-4 mt-4">
              <div className="flex items-center gap-2">
                <Badge className={getPriorityColor(viewNotice.priority)}>
                  {viewNotice.priority}
                </Badge>
                <Badge variant={viewNotice.status === "published" ? "default" : "secondary"}>
                  {capitalizeFirst(viewNotice.status)}
                </Badge>
              </div>
              <p className="text-slate-600 whitespace-pre-wrap">{viewNotice.content}</p>
              <div className="pt-4 border-t text-sm text-slate-500">
                <p>Posted: {formatDate(viewNotice.created_at)}</p>
                {viewNotice.expiry_date && (
                  <p>Expires: {formatDate(viewNotice.expiry_date)}</p>
                )}
              </div>
              {viewNotice.status === "published" && viewNotice.author_id !== user?.id && (
                <Button
                  className="w-full"
                  onClick={() => {
                    handleAcknowledge(viewNotice.id);
                    setViewNotice(null);
                  }}
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Acknowledge
                </Button>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default NoticesPage;
